/**
 * Browser auth tokens (plan §7). The backend is the only thing that talks to GoTrue; the browser keeps:
 *
 *   auth_token       short-lived access token (sent as `Authorization: Bearer …`)
 *   auth_token_exp   its expiry, epoch milliseconds
 *   refresh_token    exchanged at POST /api/auth/refresh for a new pair (GoTrue rotates it)
 *   auth_epoch       bumped whenever a session ends or is replaced (sign-out, sign-in, password change). A refresh
 *                    that started under an older epoch never writes its tokens back and never clears newer ones.
 *
 * refreshTokens() is single-flight: one request per tab (a shared promise) and, where the browser supports
 * navigator.locks, one per browser (other tabs wait and then reuse the tokens the first tab stored).
 */
import axios from 'axios';

const ACCESS = 'auth_token';
const ACCESS_EXP = 'auth_token_exp';
const REFRESH = 'refresh_token';
const EPOCH = 'auth_epoch';
const LOCK_NAME = 'appd-auth-refresh';
const REFRESH_TIMEOUT_MS = 15 * 1000;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const ACCESS_TOKEN_KEY = ACCESS;
export const AUTH_EPOCH_KEY = EPOCH;

function read(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key, value) {
  try {
    if (value === null || value === undefined || value === '') localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* storage unavailable (private mode): tokens live only for this page */
  }
}

export function getAccessToken() {
  return read(ACCESS);
}

export function getRefreshToken() {
  return read(REFRESH);
}

export function getAccessExpiry() {
  const raw = parseInt(read(ACCESS_EXP) || '0', 10);
  return Number.isFinite(raw) ? raw : 0;
}

function currentEpoch() {
  return read(EPOCH) || '0';
}

function bumpEpoch() {
  write(EPOCH, String((parseInt(currentEpoch(), 10) || 0) + 1));
}

/** Store a rotated token pair for the CURRENT session (a /refresh response). Ignores empty responses. */
export function setTokens(data) {
  if (!data || !data.token) return;
  write(ACCESS, data.token);
  if (data.refresh_token) write(REFRESH, data.refresh_token);
  const seconds = Number(data.expires_in) || 3600;
  write(ACCESS_EXP, String(Date.now() + seconds * 1000));
}

/**
 * Install the tokens of a NEW session (/login, /register, /update-password). The epoch moves first, so a refresh
 * still in flight for the previous session discards its result instead of overwriting these tokens; the previous
 * session is then ended on the server (best effort).
 */
export function replaceTokens(data) {
  if (!data || !data.token) return;
  const oldAccess = getAccessToken();
  const oldRefresh = getRefreshToken();
  bumpEpoch();
  write(REFRESH, null); // never pair the new access token with the old session's refresh token
  setTokens(data);
  if (oldAccess && oldAccess !== data.token) revokeSession(oldAccess, oldRefresh);
}

/** Forget every token and invalidate any refresh that is still in flight. */
export function clearTokens() {
  write(ACCESS, null);
  write(ACCESS_EXP, null);
  write(REFRESH, null);
  bumpEpoch();
}

/**
 * Clear the tokens only if they still belong to the request that failed with `accessToken`. A newer session that a
 * sign-in or another tab stored meanwhile is left alone. Returns true when the tokens were cleared.
 */
export function clearTokensIfCurrent(accessToken) {
  const current = getAccessToken();
  if (current && accessToken && current !== accessToken) return false;
  clearTokens();
  return true;
}

/**
 * Remove what the Supabase-era client left behind: its `sb-*` session keys, the unused `token` key, and an
 * access token without a refresh token (a pre-migration GoTrue JWT that can no longer be refreshed). Theme
 * preferences and everything else are kept.
 */
/** The sign-in generation: moved by every sign-in, sign-up and sign-out in any tab (not by a token refresh). */
export function currentAuthEpoch() {
  return read(EPOCH);
}

/**
 * Remove what the Supabase-era frontend left behind. Returns true when that ended the visitor's session: a
 * supabase-js `sb-<ref>-auth-token`, or an access token without the refresh token every current session has, and
 * no current session left afterwards. The caller then says the session expired instead of silently showing the
 * login page. A stray old key next to a current session is just removed (returns false): that visitor stays in.
 */
export function cleanupLegacyAuthKeys() {
  let droppedSession = false;
  try {
    const doomed = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith('sb-')) doomed.push(key);
    }
    const hadLegacySession = doomed.some((key) => key.endsWith('-auth-token'));
    doomed.forEach((key) => localStorage.removeItem(key));
    localStorage.removeItem('token');
    if (read(ACCESS) && !read(REFRESH)) {
      write(ACCESS, null);
      write(ACCESS_EXP, null);
      droppedSession = true;
    }
    if (hadLegacySession && !read(REFRESH)) droppedSession = true;
  } catch {
    /* storage unavailable */
  }
  return droppedSession;
}

export class RefreshError extends Error {
  /**
   * code: 'refresh_rejected' (the session is over) or 'refresh_unavailable' (try again later, keep the tokens).
   * superseded: the session was already ended or replaced in this browser (sign-out, another sign-in), so the
   * caller must neither clear tokens nor redirect: whatever is stored now is newer than this refresh.
   */
  constructor(code, message, superseded = false) {
    super(message || code);
    this.code = code;
    this.superseded = superseded;
  }
}

let inFlight = null;

async function doRefresh() {
  const startEpoch = currentEpoch();
  const startToken = getRefreshToken();
  if (!startToken) throw new RefreshError('refresh_rejected', 'No refresh token');

  const run = async () => {
    if (currentEpoch() !== startEpoch) throw new RefreshError('refresh_rejected', 'Session replaced', true);
    const nowToken = getRefreshToken();
    if (!nowToken) throw new RefreshError('refresh_rejected', 'Signed out');
    // Another tab refreshed while we waited for the lock: reuse its result.
    if (nowToken !== startToken && getAccessExpiry() - Date.now() > 60 * 1000) {
      return { token: getAccessToken(), refresh_token: nowToken };
    }
    let res;
    try {
      res = await axios.post(
        `${BACKEND_URL}/api/auth/refresh`,
        { refresh_token: nowToken },
        { timeout: REFRESH_TIMEOUT_MS },
      );
    } catch (err) {
      if (currentEpoch() !== startEpoch) throw new RefreshError('refresh_rejected', 'Session replaced', true);
      if (err?.response?.status === 401) {
        // The server ended this session. Forget it, unless something newer was stored while we waited.
        if (getRefreshToken() === nowToken) clearTokens();
        throw new RefreshError('refresh_rejected', 'Session ended');
      }
      throw new RefreshError('refresh_unavailable', 'Refresh failed');
    }
    if (currentEpoch() !== startEpoch) {
      // Signed out (or signed in as someone else) while this request was in flight: end the session we just got.
      revokeSession(res.data?.token, res.data?.refresh_token);
      throw new RefreshError('refresh_rejected', 'Session replaced', true);
    }
    setTokens(res.data);
    return res.data;
  };

  if (typeof navigator !== 'undefined' && navigator.locks && navigator.locks.request) {
    return navigator.locks.request(LOCK_NAME, run);
  }
  return run();
}

/** Get a fresh token pair. Concurrent callers in this tab share one request. */
export function refreshTokens() {
  if (!inFlight) {
    inFlight = doRefresh().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

/** Best-effort server-side logout that survives page unloads (keepalive). Never throws. */
export function revokeSession(accessToken, refreshToken) {
  if (!accessToken) return;
  try {
    fetch(`${BACKEND_URL}/api/auth/logout`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ refresh_token: refreshToken || '' }),
    }).catch(() => {});
  } catch {
    /* ignore */
  }
}
